/**
 * Capture an inline SVG element as a PNG Blob.
 *
 * Uses XMLSerializer to serialize the SVG, draws it onto a Canvas,
 * and exports as PNG. Works with all inline SVG content (no external CSS issues).
 *
 * @param svgElement - The SVG DOM element to capture
 * @param scale - Resolution multiplier (default 2 for retina quality)
 * @returns Promise<Blob> - PNG image blob
 */
export async function captureSvgAsPng(
  svgElement: SVGSVGElement,
  scale: number = 2
): Promise<Blob> {
  // Get SVG dimensions
  const svgWidth = svgElement.getAttribute('width')
    ? parseInt(svgElement.getAttribute('width')!, 10)
    : svgElement.getBoundingClientRect().width
  const svgHeight = svgElement.getAttribute('height')
    ? parseInt(svgElement.getAttribute('height')!, 10)
    : svgElement.getBoundingClientRect().height

  // Serialize SVG to string
  const serializer = new XMLSerializer()
  let svgString = serializer.serializeToString(svgElement)

  // Ensure XML namespace is present
  if (!svgString.includes('xmlns="http://www.w3.org/2000/svg"')) {
    svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
  }

  // Create a Blob from the SVG string
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)

  return new Promise<Blob>((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      // Create canvas at scaled resolution
      const canvas = document.createElement('canvas')
      canvas.width = svgWidth * scale
      canvas.height = svgHeight * scale

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(url)
        reject(new Error('Could not get canvas 2D context'))
        return
      }

      // Fill white background (SVG may have transparent bg)
      ctx.fillStyle = '#FFFFFF'
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      // Scale and draw
      ctx.scale(scale, scale)
      ctx.drawImage(img, 0, 0, svgWidth, svgHeight)

      // Export as PNG blob
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url)
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Canvas toBlob returned null'))
          }
        },
        'image/png',
        1.0
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load SVG as image'))
    }

    img.src = url
  })
}
